import { mergeWith, cloneDeep, isFinite } from "lodash"
import type { SingleDateGathererDataItem } from "gatherers/types"
import type { TupleN } from "helpers/types/helpers"

/**
 * Adds a delta column comparing two data items.
 * Returns [original, comparison, delta] where delta = first - second for values,
 * second - first for summaries.
 * @param data - Tuple of exactly 2 gatherer data items for delta calculation
 * @returns Array of 3 items: original two plus delta item, maintaining Record structure
 */
export function addDeltaColumn(
  data: TupleN<SingleDateGathererDataItem, 2>,
): TupleN<SingleDateGathererDataItem, 3> {
  const [first, second] = data

  const delta = mergeWith(
    cloneDeep(first),
    second,
    (existingValue, newValue) => {
      if (isFinite(newValue) && !isFinite(existingValue)) {
        return -newValue
      }
      return !isFinite(existingValue) || !isFinite(newValue)
        ? undefined
        : existingValue - newValue
    },
  )

  const performanceSummaryDelta =
    isFinite(first.summary) && isFinite(second.summary)
      ? (second.summary as number) - (first.summary as number)
      : undefined

  return [
    first,
    second,
    {
      values: delta.values,
      summary: performanceSummaryDelta,
    },
  ]
}
